import { ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { fadeUp, press } from "@/lib/motion";

interface SectionProps {
  title?: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** iOS grouped inset list section. */
export function Section({ title, action, footer, children, className }: SectionProps) {
  return (
    <motion.section variants={fadeUp} className={cn("space-y-2", className)}>
      {(title || action) && (
        <div className="flex items-end justify-between px-4">
          {title && (
            <h2 className="text-footnote font-medium tracking-wide text-muted uppercase">
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      <div className="divide-y-[0.5px] divide-separator overflow-hidden rounded-md bg-surface">
        {children}
      </div>
      {footer && <p className="px-4 text-footnote text-muted">{footer}</p>}
    </motion.section>
  );
}

interface RowProps {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  className?: string;
}

export function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
  chevron,
  className,
}: RowProps) {
  const content = (
    <>
      {leading && <div className="shrink-0">{leading}</div>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-body">{title}</div>
        {subtitle && <div className="truncate text-footnote text-muted">{subtitle}</div>}
      </div>
      {trailing && <div className="shrink-0 text-right text-body">{trailing}</div>}
      {chevron && <ChevronRight className="size-4 shrink-0 text-muted/60" aria-hidden />}
    </>
  );
  const cls = cn("flex min-h-[52px] w-full items-center gap-3 px-4 py-2.5 text-left", className);
  if (!onClick) return <div className={cls}>{content}</div>;
  return (
    <motion.button
      type="button"
      whileTap={press}
      onClick={onClick}
      className={cn(cls, "transition-colors active:bg-surface-2")}
    >
      {content}
    </motion.button>
  );
}
